import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, List, ListItem, ListItemText, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Chip, Typography,
  Alert, CircularProgress, FormControlLabel, Switch, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Email as EmailIcon, Star as StarIcon, StarBorder as StarBorderIcon,
  MoveToInbox as MigrateIcon
} from '@mui/icons-material';
import { supabase } from '../../utils/supabaseClient';

const EMAIL_TYPES = [
  { value: 'primary', label: 'Primary', color: 'primary' },
  { value: 'billing', label: 'Billing', color: 'secondary' },
  { value: 'operations', label: 'Operations', color: 'info' },
  { value: 'management', label: 'Management', color: 'success' },
  { value: 'other', label: 'Other', color: 'default' }
];

// Helper function to get auth headers
const getAuthHeaders = async () => {
  const { data: authData } = await supabase.auth.getSession();
  const token = authData?.session?.access_token;

  if (!token) {
    throw new Error('No authentication token available');
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
};

const MultipleEmailManager = ({ providerId, legacyEmail, onEmailsChange }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailType, setNewEmailType] = useState('primary');
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState('');
  const [showMigration, setShowMigration] = useState(false);

  useEffect(() => {
    if (providerId) {
      fetchEmails();
    } else {
      // No provider ID yet (add mode), so just set loading to false
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    // Show migration option if there's a legacy email but no emails in the new system
    setShowMigration(Boolean(legacyEmail && emails.length === 0));
  }, [legacyEmail, emails]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      
      const response = await fetch(`/api/provider-emails/${providerId}`, {
        headers
      });
      const data = await response.json();
      
      if (data.success) {
        const emailList = Array.isArray(data.emails) ? data.emails : [];
        setEmails(emailList);
        onEmailsChange?.(emailList);
      } else {
        console.error('Failed to fetch emails:', data.message);
        setError('Failed to load email addresses');
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
      setError('Failed to load email addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateLegacyEmail = async () => {
    try {
      setError('');
      const headers = await getAuthHeaders();
      
      const response = await fetch(`/api/provider-emails/migrate/${providerId}`, {
        method: 'POST',
        headers
      });
      
      const data = await response.json();
      
      if (data.success && data.migrated) {
        await fetchEmails();
        setShowMigration(false);
      } else if (!data.migrated) {
        setError(data.message || 'Email already migrated or no email to migrate');
      }
    } catch (error) {
      setError('Failed to migrate legacy email');
    }
  };

  const handleAddEmail = () => {
    setEditingEmail(null);
    setNewEmail('');
    setNewEmailType('primary');
    setIsPrimary(emails.length === 0); // First email is primary by default
    setDialogOpen(true);
  };

  const handleEditEmail = (email) => {
    setEditingEmail(email);
    setNewEmail(email.email);
    setNewEmailType(email.email_type);
    setIsPrimary(email.is_primary);
    setDialogOpen(true);
  };

  const handleSaveEmail = async () => {
    try {
      setError('');
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
        setError('Please enter a valid email address');
        return;
      }
      
      // If no provider ID yet, show error
      if (!providerId) {
        setError('Please save the provider first before adding email addresses');
        return;
      }
      
      const emailData = {
        email: newEmail.trim(),
        email_type: newEmailType,
        is_primary: isPrimary
      };

      const headers = await getAuthHeaders();
      let response;
      
      if (editingEmail) {
        // Update existing email
        response = await fetch(`/api/provider-emails/${editingEmail.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(emailData)
        });
      } else {
        // Add new email
        response = await fetch('/api/provider-emails', {
          method: 'POST',
          headers,
          body: JSON.stringify({ provider_id: providerId, ...emailData })
        });
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to save email');
      }
      
      await fetchEmails();
      setDialogOpen(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    try {
      setError('');
      const headers = await getAuthHeaders();
      
      const response = await fetch(`/api/provider-emails/${emailId}`, {
        method: 'DELETE',
        headers
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to delete email');
      }
      
      await fetchEmails();
    } catch (error) {
      setError(error.message);
    }
  };

  const getEmailTypeConfig = (type) => {
    return EMAIL_TYPES.find(t => t.value === type) || EMAIL_TYPES[4]; // Default to 'other'
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading emails...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
          Email Addresses ({emails.length})
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddEmail}
          disabled={!providerId}
        >
          Add Email
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {showMigration && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<MigrateIcon />}
              onClick={handleMigrateLegacyEmail}
            >
              Migrate
            </Button>
          }
        >
          Legacy email found: <strong>{legacyEmail}</strong>. Click migrate to add it to the new system.
        </Alert>
      )}

      {emails.length === 0 ? (
        <Alert severity={providerId ? "info" : "warning"}>
          {providerId 
            ? "No email addresses configured. Add an email address to enable PDF delivery."
            : "Save the provider first, then you can add email addresses."
          }
          {legacyEmail && !showMigration && providerId && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Note: Legacy email "{legacyEmail}" found but not migrated.
            </Typography>
          )}
        </Alert>
      ) : (
        <List dense>
          {emails.map((email) => {
            const typeConfig = getEmailTypeConfig(email.email_type);
            return (
              <ListItem key={email.id} divider>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  {email.is_primary ? (
                    <StarIcon color="warning" fontSize="small" />
                  ) : (
                    <EmailIcon color="action" fontSize="small" />
                  )}
                </Box>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{email.email}</Typography>
                      <Chip
                        label={typeConfig.label}
                        color={typeConfig.color}
                        size="small"
                        variant="outlined"
                      />
                      {email.is_primary && (
                        <Chip
                          label="Primary"
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    onClick={() => handleEditEmail(email)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteEmail(email.id)}
                    disabled={emails.length === 1} // Don't allow deleting the last email
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Add/Edit Email Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEmail ? 'Edit Email Address' : 'Add Email Address'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email Address"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              fullWidth
              required
              error={Boolean(error && error.includes('valid email'))}
              helperText={error && error.includes('valid email') ? error : ''}
            />
            
            <FormControl fullWidth>
              <InputLabel>Email Type</InputLabel>
              <Select
                value={newEmailType}
                label="Email Type"
                onChange={(e) => setNewEmailType(e.target.value)}
              >
                {EMAIL_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{type.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Divider />
            
            <FormControlLabel
              control={
                <Switch
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isPrimary ? <StarIcon color="warning" /> : <StarBorderIcon />}
                  <Typography>Primary Email</Typography>
                </Box>
              }
            />
            
            <Typography variant="caption" color="text.secondary">
              The primary email is used as the default for sending PDF statements. 
              Only one email can be marked as primary per provider.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveEmail} 
            variant="contained"
            disabled={!newEmail.trim()}
          >
            {editingEmail ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MultipleEmailManager; 